import * as THREE from 'three';
import { BLOCK_CAPS, type LightBlock } from '@vrlearning/lesson-schema';
import type { WorldBuilder } from '../world.js';
import type { StatefulBlock } from './features.js';

/**
 * The light block (V2): lamps (at most BLOCK_CAPS.lights that really light, decision 6; any up to 24 as
 * halos only), their brightness `level`, and the world's sun and sky light as shares (`sun`, `sky`).
 * Several light blocks multiply their shares.
 */
export function buildLightBlock(
  world: WorldBuilder,
  block: LightBlock,
  name: string,
): StatefulBlock {
  let level = block.level;
  const lights: { light: THREE.SpotLight | THREE.PointLight; intensity: number }[] = [];
  block.lamps.forEach((lamp, i) => {
    if (lamp.intensity > 0 && world.litLamps < BLOCK_CAPS.lights) {
      world.litLamps++;
      const light = lamp.aim
        ? new THREE.SpotLight(
            lamp.colour,
            0,
            lamp.range,
            THREE.MathUtils.degToRad(lamp.angle),
            0.7,
            2,
          )
        : new THREE.PointLight(lamp.colour, 0, lamp.range, 2);
      light.name = `${name}-lamp-${i}`;
      light.position.set(...lamp.at);
      light.castShadow = false;
      world.root.add(light);
      if (light instanceof THREE.SpotLight && lamp.aim) {
        light.target.position.set(...lamp.aim);
        world.root.add(light.target);
      }
      lights.push({ light, intensity: lamp.intensity });
    }
    if (lamp.halo > 0) {
      const holder = new THREE.Object3D();
      holder.position.set(...lamp.at);
      world.root.add(holder);
      const phase = i * 1.7;
      world.addHalo({
        on: holder,
        centre: new THREE.Vector3(),
        radius: lamp.halo,
        colour: new THREE.Color(lamp.colour),
        flicker: (t) =>
          level * (1 - lamp.flicker + lamp.flicker * (0.5 + 0.5 * Math.sin(t * 2.3 + phase))),
      });
    }
  });
  const shares = { sun: block.sun, sky: block.sky };
  world.lightShares.push(shares);
  const apply = (): void => {
    for (const { light, intensity } of lights) light.intensity = intensity * level;
    world.light.sun = world.lightShares.reduce((product, s) => product * s.sun, 1);
    world.light.environment = world.lightShares.reduce((product, s) => product * s.sky, 1);
  };
  apply();
  return {
    set(setting, value) {
      if (setting === 'level') level = value as number;
      else if (setting === 'sun') shares.sun = value as number;
      else if (setting === 'sky') shares.sky = value as number;
      apply();
    },
  };
}
